import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    Search,
    RotateCcw,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Eye,
    Activity,
    Play,
    Target,
    Trash2,
    Award,
    Receipt,
    GraduationCap,
    X,
} from 'lucide-react';
import Moment from 'react-moment';
import moment from 'moment-timezone';
import { AppLayout } from '../../../components/layouts';
import PageHeader from '../../../components/dashboard/PageHeader';
import { CustomModal } from '../../../components/ui';
import { useRegistration } from '../../../components/layouts/app/RegistrationContext';
import UserService from '../../../services/user.service';
import { toast } from 'react-toastify';
import { formatDate } from '../../../components/custom-hooks';
import { calculateExactAge } from '../../../utils/Tools';
import { Days } from '../../../utils/constants';
import { buildPracticeLink } from '../../../utils/practiceLink';
import { getChildStatus, statusBadgeClass } from '../../../utils/childHelpers';
import { setResume } from '../../../redux/reducers/userSlice';
import { startPersonalCheckout } from '../../../utils/personalCheckout';
import ProgramsList from '../../../components/Certificate/ProgramsList';
import ReceiptList from '../../../components/Certificate/ReceiptList';
import {
    modalBody,
    modalCloseBtn,
    modalDangerBtn,
    modalSecondaryBtn,
    modalShell,
    modalSubtitle,
    modalTitle,
} from '../../../components/ui/modalDesign';

const PAGE_SIZE = 10;

function latestProgram(child: any) {
    if (!Array.isArray(child?.programs) || child.programs.length === 0) return null;
    return child.programs[child.programs.length - 1];
}

function ChildAvatar({ firstName, lastName }: { firstName?: string; lastName?: string }) {
    const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || '?';
    return (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-ryd-primary to-[#6B1F66] text-[13px] font-bold text-white shadow-sm">
            {initials}
        </div>
    );
}

export default function ChildrenPage() {
    const userService = new UserService();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { openAddChildWithWarning } = useRegistration();

    const [childrenArr, setChildrenArr] = useState<any[]>([]);
    const [unpaidLessonByChild, setUnpaidLessonByChild] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [levelFilter, setLevelFilter] = useState('all');
    const [programFilter, setProgramFilter] = useState('all');
    const [sortAsc, setSortAsc] = useState(true);
    const [page, setPage] = useState(1);
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const [selectedChild, setSelectedChild] = useState<any>({});
    const [toggleDel, setToggleDel] = useState(false);
    const [toggleCertificate, setToggleCertificate] = useState(false);
    const [toggleReceipt, setToggleReceipt] = useState(false);
    const [programs, setPrograms] = useState([]);
    const [paidPrograms, setPaidPrograms] = useState([]);
    const [testLaunchingId, setTestLaunchingId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);

    const getChildren = async () => {
        setLoading(true);
        try {
            const [response, lessons] = await Promise.all([
                userService.getAllChildren().catch((err) => {
                    toast.error(err?.message || 'Unable to load children');
                    return null;
                }),
                userService.getMyPersonalClasses().catch(() => null),
            ]);
            if (Array.isArray(response?.data)) setChildrenArr(response.data);
            const pending: Record<string, any> = {};
            if (Array.isArray(lessons?.data)) {
                lessons.data.forEach((row: any) => {
                    if (row.paymentStatus === 'unpaid' && !row.cancelledAt) {
                        pending[String(row.childId || row.child?.id)] = row;
                    }
                });
            }
            setUnpaidLessonByChild(pending);
        } catch (err: any) {
            toast.error(err?.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getChildren();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const programOptions = useMemo(() => {
        const titles = new Set<string>();
        childrenArr.forEach((c) => {
            (c.programs || []).forEach((p: any) => {
                if (p?.package?.title) titles.add(p.package.title);
            });
        });
        return Array.from(titles);
    }, [childrenArr]);

    const levelOptions = useMemo(() => {
        const levels = new Set<string>();
        childrenArr.forEach((c) => {
            const p = latestProgram(c);
            if (p?.package?.level != null) levels.add(String(p.package.level));
        });
        return Array.from(levels).sort((a, b) => Number(a) - Number(b));
    }, [childrenArr]);

    const filtered = useMemo(() => {
        let list = [...childrenArr];
        const q = search.trim().toLowerCase();
        if (q) {
            list = list.filter(
                (c) =>
                    c.firstName?.toLowerCase().includes(q) ||
                    c.lastName?.toLowerCase().includes(q) ||
                    `${c.firstName} ${c.lastName}`.toLowerCase().includes(q)
            );
        }
        if (statusFilter !== 'all') {
            list = list.filter((c) => getChildStatus(c, latestProgram(c)) === statusFilter);
        }
        if (levelFilter !== 'all') {
            list = list.filter((c) => String(latestProgram(c)?.package?.level ?? '') === levelFilter);
        }
        if (programFilter !== 'all') {
            list = list.filter((c) =>
                (c.programs || []).some((p: any) => p?.package?.title === programFilter)
            );
        }
        list.sort((a, b) => {
            const an = `${a.firstName} ${a.lastName}`.toLowerCase();
            const bn = `${b.firstName} ${b.lastName}`.toLowerCase();
            return sortAsc ? an.localeCompare(bn) : bn.localeCompare(an);
        });
        return list;
    }, [childrenArr, search, statusFilter, levelFilter, programFilter, sortAsc]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const rangeStart = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(currentPage * PAGE_SIZE, filtered.length);

    useEffect(() => {
        setPage(1);
    }, [search, statusFilter, levelFilter, programFilter]);

    const resetFilters = () => {
        setSearch('');
        setStatusFilter('all');
        setLevelFilter('all');
        setProgramFilter('all');
        setSortAsc(true);
        setPage(1);
    };

    const handleResume = (data: any) => {
        dispatch(setResume(data));
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const response = await userService.deleteChild(selectedChild?.id);
            if (!response.status) {
                toast.error(response?.message);
                return;
            }
            toast.success(`${selectedChild?.firstName} has been deleted!`);
            setToggleDel(false);
            getChildren();
        } catch (err: any) {
            toast.error(err?.message);
        } finally {
            setDeleting(false);
        }
    };

    const practiceLink = (prog: any) => {
        const finalLink = buildPracticeLink(prog);
        if (!finalLink) {
            toast.warn("Sorry this program doesn't have a practice link yet");
            return;
        }
        window.open(finalLink, '_blank', 'noopener,noreferrer');
    };

    const handleTestLaunch = async (prog: any) => {
        if (!prog?.package?.cbtPracticeId) {
            toast.warn('No package test is assigned for this program yet');
            return;
        }
        const popup = window.open('about:blank', '_blank');
        setTestLaunchingId(prog.id);
        try {
            const response = await userService.launchProgramPractice(prog.id);
            if (response?.status && response?.data?.url) {
                if (popup) {
                    try {
                        popup.opener = null;
                    } catch {
                        /* ignore */
                    }
                    popup.location.href = response.data.url;
                } else {
                    window.open(response.data.url, '_blank', 'noopener,noreferrer');
                }
                return;
            }
            popup?.close();
            toast.error(response?.message || 'Unable to open test');
        } catch (err: any) {
            popup?.close();
            toast.error(err?.message || 'Unable to open test');
        } finally {
            setTestLaunchingId(null);
        }
    };

    const triggerCertificate = async (data: any) => {
        try {
            const response = await userService.getChildPrograms(data.id);
            if (!response.status) {
                toast.error('Try again later');
                return;
            }
            setPrograms(response.data);
            setToggleCertificate(true);
        } catch (err: any) {
            toast.error(err?.message);
        }
    };

    const triggerReceipt = async (data: any) => {
        setSelectedChild(data);
        try {
            const response = await userService.getChildPaidPrograms(data.id);
            if (!response.status) {
                toast.error('Try again later');
                return;
            }
            setPaidPrograms(response.data);
            setToggleReceipt(true);
        } catch (err: any) {
            toast.error(err?.message);
        }
    };

    const selectClass =
        'w-full min-w-0 rounded-xl border border-[#efe6f1] bg-white px-3 py-2.5 text-[13px] text-[#334155] outline-none focus:border-ryd-primary lg:w-auto';
    const actionIconBtn =
        'inline-flex shrink-0 items-center justify-center gap-1 rounded-xl border px-2 py-1.5 text-[11px] font-semibold whitespace-nowrap transition hover:scale-[1.02]';

    const renderExpanded = (item: any) => {
        const prog = latestProgram(item);
        const pTime = moment();
        if (prog) {
            pTime.day(prog.day);
            pTime.hour(prog.time);
            pTime.second(0);
            pTime.minute(0);
        }
        const teacherName = [prog?.teacher?.firstName, prog?.teacher?.lastName].filter(Boolean).join(' ') || '—';
        const details = [
            { label: 'Current Level', value: prog?.package?.title || '—' },
            { label: 'Gender', value: item?.gender || '—' },
            { label: 'Program', value: prog?.package?.title || '—' },
            { label: 'Teacher', value: teacherName },
            { label: 'Day', value: prog ? Days[prog.day] || '—' : '—' },
            {
                label: 'Time',
                value: prog ? <Moment format="hh:mm A" date={pTime.toISOString()} /> : '—',
            },
            { label: 'Cohort', value: prog?.cohort?.title || '—' },
            {
                label: 'Start Date',
                value: prog?.cohort?.startDate ? formatDate(prog.cohort.startDate) : '—',
            },
            { label: 'Enrollment Date', value: item?.createdAt ? formatDate(item.createdAt) : '—' },
            { label: 'Attendance %', value: prog?.attendance != null ? `${prog.attendance}%` : '—' },
            { label: 'Assignments Completed', value: '—' },
            { label: 'Projects Submitted', value: prog?.projectUrl ? '1+' : '0' },
            { label: 'Certificates', value: prog?.isCompleted ? 'Available' : '—' },
        ];

        return (
            <div className="border-t border-[#f0eaf0] bg-gradient-to-b from-[#FBF8FC] to-white px-4 py-5 sm:px-6">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {details.map((d) => (
                        <div key={d.label} className="rounded-xl border border-[#efe6f1] bg-white px-3.5 py-3">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#94a3b8]">{d.label}</p>
                            <p className="mt-1 text-[13px] font-semibold capitalize text-[#334155]">{d.value}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <p className="mr-2 w-full text-[11px] font-semibold uppercase tracking-wide text-[#8b9aab] sm:w-auto">
                        Quick actions
                    </p>
                    {unpaidLessonByChild[String(item.id)] ? (
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    const response = await userService.payPersonalClass(unpaidLessonByChild[String(item.id)].id);
                                    if (!response?.status) {
                                        toast.error(response?.message || 'Unable to open checkout');
                                        return;
                                    }
                                    startPersonalCheckout(response.data);
                                } catch (err: any) {
                                    toast.error(err?.message || 'Unable to open checkout');
                                }
                            }}
                            className={`${actionIconBtn} border-[#14507a] bg-[#f3f8fc] text-[#14507a]`}
                        >
                            Complete registration
                        </button>
                    ) : null}
                    {(!item.programs || item.programs.length === 0) ? (
                        <>
                            <button
                                type="button"
                                onClick={() => handleResume(item)}
                                className={`${actionIconBtn} border-emerald-200 bg-emerald-50 text-emerald-700`}
                            >
                                <Play size={13} /> Resume
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedChild(item);
                                    setToggleDel(true);
                                }}
                                className={`${actionIconBtn} border-red-200 bg-red-50 text-red-700`}
                            >
                                <Trash2 size={13} /> Remove
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => navigate(`/parent/activity?child=${item.id}`)}
                                className={`${actionIconBtn} border-ryd-primary/30 bg-ryd-primaryLess1 text-ryd-primary`}
                            >
                                <Activity size={13} /> View Activity
                            </button>
                            <button
                                type="button"
                                onClick={() => practiceLink(prog)}
                                className={`${actionIconBtn} border-blue-200 bg-blue-50 text-blue-700`}
                            >
                                <Target size={13} /> Practice
                            </button>
                            {prog?.package?.cbtPracticeId ? (
                                <button
                                    type="button"
                                    disabled={testLaunchingId === prog.id}
                                    onClick={() => void handleTestLaunch(prog)}
                                    className={`${actionIconBtn} border-indigo-200 bg-indigo-50 text-indigo-700 disabled:opacity-60`}
                                >
                                    <Play size={13} /> {testLaunchingId === prog.id ? 'Opening…' : 'Continue Learning'}
                                </button>
                            ) : null}
                            <button
                                type="button"
                                onClick={() => triggerCertificate(item)}
                                className={`${actionIconBtn} border-emerald-200 bg-emerald-50 text-emerald-700`}
                            >
                                <Award size={13} /> Certificate
                            </button>
                            <button
                                type="button"
                                onClick={() => triggerReceipt(item)}
                                className={`${actionIconBtn} border-purple-200 bg-purple-50 text-purple-700`}
                            >
                                <Receipt size={13} /> Receipt
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        onClick={() => setExpandedId(null)}
                        className={`${actionIconBtn} ml-auto border-[#e8dce8] bg-white text-[#64748b]`}
                    >
                        <X size={13} /> Collapse
                    </button>
                </div>
            </div>
        );
    };

    const renderRowActions = (item: any, stop: (e: React.MouseEvent) => void) => {
        const prog = latestProgram(item);
        if (!item.programs || item.programs.length === 0) {
            return (
                <div className="flex flex-nowrap items-center gap-1.5" onClick={stop}>
                    <button
                        type="button"
                        title="Resume"
                        onClick={() => handleResume(item)}
                        className={`${actionIconBtn} border-emerald-200 bg-emerald-50 text-emerald-700`}
                    >
                        <Play size={13} /> Resume
                    </button>
                    <button
                        type="button"
                        title="Remove"
                        onClick={() => {
                            setSelectedChild(item);
                            setToggleDel(true);
                        }}
                        className={`${actionIconBtn} border-red-200 bg-red-50 text-red-700`}
                    >
                        <Trash2 size={13} /> Remove
                    </button>
                </div>
            );
        }
        return (
            <div className="flex flex-nowrap items-center gap-1.5" onClick={stop}>
                <button
                    type="button"
                    title="View"
                    onClick={() => setExpandedId(item.id === expandedId ? null : item.id)}
                    className={`${actionIconBtn} border-[#e8dce8] bg-white text-[#475569]`}
                >
                    <Eye size={13} />
                </button>
                <button
                    type="button"
                    title="Activity"
                    onClick={() => navigate(`/parent/activity?child=${item.id}`)}
                    className={`${actionIconBtn} border-ryd-primary/30 bg-ryd-primaryLess1 text-ryd-primary`}
                >
                    <Activity size={13} />
                </button>
                <button
                    type="button"
                    title="Practice"
                    onClick={() => practiceLink(prog)}
                    className={`${actionIconBtn} border-blue-200 bg-blue-50 text-blue-700`}
                >
                    <Target size={13} />
                </button>
            </div>
        );
    };

    return (
        <AppLayout>
            <PageHeader
                title="My Children"
                subtitle="Manage your children's learning journey and track their progress."
                actionLabel="Add New Child"
                onAction={openAddChildWithWarning}
            />

            <div className="mb-4 flex flex-col gap-3 rounded-[20px] border border-[#efe6f1] bg-white p-3 shadow-[0_8px_30px_rgba(136,22,122,0.04)] sm:p-4 lg:flex-row lg:items-center">
                <div className="relative min-w-0 flex-1">
                    <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by child name"
                        className="w-full rounded-xl border border-[#efe6f1] bg-[#FBF8FC] py-2.5 pl-10 pr-3 text-[13px] outline-none focus:border-ryd-primary"
                    />
                </div>
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:contents">
                    <select className={selectClass} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">All statuses</option>
                        {['Active', 'Completed', 'Incomplete', 'Inactive', 'Paused'].map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    <select className={selectClass} value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
                        <option value="all">All levels</option>
                        {levelOptions.map((l) => (
                            <option key={l} value={l}>Level {l}</option>
                        ))}
                    </select>
                    <select className={`col-span-2 sm:col-span-1 ${selectClass}`} value={programFilter} onChange={(e) => setProgramFilter(e.target.value)}>
                        <option value="all">All programs</option>
                        {programOptions.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={() => setSortAsc((v) => !v)}
                        className={`${selectClass} whitespace-nowrap`}
                    >
                        Sort {sortAsc ? 'A–Z' : 'Z–A'}
                    </button>
                    <button
                        type="button"
                        onClick={resetFilters}
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#efe6f1] px-3 py-2.5 text-[13px] font-medium text-[#64748b] hover:border-ryd-primary hover:text-ryd-primary lg:w-auto"
                    >
                        <RotateCcw size={14} /> Reset
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="relative flex h-48 items-center justify-center">
                    <div className="h-[72px] w-[72px] animate-spin rounded-full border border-ryd-primary border-l-white" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="rounded-[22px] border border-dashed border-[#e8dce8] bg-white px-6 py-20 text-center shadow-sm">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-ryd-primaryLess1 text-ryd-primary">
                        <GraduationCap size={28} />
                    </div>
                    <h3 className="font-[AvertaStd-Semibold] text-[20px] text-ryd-headerTextPrimary">
                        No children added yet
                    </h3>
                    <p className="mx-auto mt-2 max-w-md text-[14px] text-[#8b9aab]">
                        Start your child&apos;s learning journey by adding your first child.
                    </p>
                    <button
                        type="button"
                        onClick={openAddChildWithWarning}
                        className="mt-6 inline-flex rounded-[14px] bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE] px-6 py-3 text-[14px] font-semibold text-white shadow-md"
                    >
                        Add New Child
                    </button>
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden overflow-hidden rounded-[22px] border border-[#efe6f1] bg-white shadow-[0_12px_40px_rgba(136,22,122,0.06)] lg:block">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[1280px] border-collapse text-left">
                                <thead>
                                    <tr className="border-b border-[#f0eaf0] bg-[#FBF8FC] text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
                                        <th className="px-4 py-4 font-semibold">Child</th>
                                        <th className="px-3 py-4 font-semibold">Age</th>
                                        <th className="px-3 py-4 font-semibold">Level</th>
                                        <th className="px-3 py-4 font-semibold">Current Level</th>
                                        <th className="px-3 py-4 font-semibold">Day</th>
                                        <th className="px-3 py-4 font-semibold">Time</th>
                                        <th className="px-3 py-4 font-semibold">Cohort</th>
                                        <th className="px-3 py-4 font-semibold">Start</th>
                                        <th className="px-3 py-4 font-semibold">Teacher</th>
                                        <th className="px-3 py-4 font-semibold">Status</th>
                                        <th className="px-4 py-4 font-semibold whitespace-nowrap">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageItems.map((item) => {
                                        const prog = latestProgram(item);
                                        const status = getChildStatus(item, prog);
                                        const pTime = moment();
                                        if (prog) {
                                            pTime.day(prog.day);
                                            pTime.hour(prog.time);
                                            pTime.second(0);
                                            pTime.minute(0);
                                        }
                                        const teacher = [prog?.teacher?.firstName, prog?.teacher?.lastName]
                                            .filter(Boolean)
                                            .join(' ') || '—';
                                        const expanded = expandedId === item.id;
                                        return (
                                            <tr key={item.id} className="group">
                                                <td colSpan={11} className="p-0">
                                                    <div
                                                        className={`cursor-pointer border-b border-[#f3eef5] transition ${
                                                            expanded ? 'bg-[#FBF8FC]' : 'hover:bg-[#FBF8FC]/80'
                                                        }`}
                                                        onClick={() => setExpandedId(expanded ? null : item.id)}
                                                    >
                                                        <div className="flex min-h-[72px] items-center">
                                                            <div className="flex w-[18%] min-w-[200px] items-center gap-3 px-4 py-3">
                                                                <ChildAvatar firstName={item.firstName} lastName={item.lastName} />
                                                                <div>
                                                                    <p className="text-[14px] font-semibold capitalize text-ryd-headerTextPrimary">
                                                                        {item.firstName} {item.lastName}
                                                                    </p>
                                                                    <p className="text-[11px] text-[#94a3b8]">
                                                                        {item.firstName} · {item.lastName}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="w-[6%] px-3 text-[13px] text-[#475569]">
                                                                {calculateExactAge(item?.age, item?.createdAt)}
                                                            </div>
                                                            <div className="w-[7%] px-3 text-[13px] font-medium text-[#475569]">
                                                                {prog?.package?.level ?? '—'}
                                                            </div>
                                                            <div className="w-[14%] min-w-[140px] px-3">
                                                                {prog?.package?.title ? (
                                                                    <span className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-gradient-to-r from-[#F3ECFF] to-[#fce7f3] px-2.5 py-1 text-[11px] font-semibold text-ryd-primary">
                                                                        <GraduationCap size={12} />
                                                                        <span className="truncate">{prog.package.title}</span>
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-[13px] text-[#94a3b8]">—</span>
                                                                )}
                                                            </div>
                                                            <div className="w-[7%] px-3 text-[13px] capitalize text-[#475569]">
                                                                {prog ? Days[prog.day] || '—' : '—'}
                                                            </div>
                                                            <div className="w-[8%] px-3 text-[13px] text-[#475569]">
                                                                {prog ? (
                                                                    <Moment format="hh:mm A" date={pTime.toISOString()} />
                                                                ) : (
                                                                    '—'
                                                                )}
                                                            </div>
                                                            <div className="w-[10%] truncate px-3 text-[13px] text-[#475569]">
                                                                {prog?.cohort?.title || '—'}
                                                            </div>
                                                            <div className="w-[9%] px-3 text-[12px] text-[#475569]">
                                                                {prog?.cohort?.startDate
                                                                    ? formatDate(prog.cohort.startDate)
                                                                    : '—'}
                                                            </div>
                                                            <div className="w-[10%] truncate px-3 text-[13px] capitalize text-[#475569]">
                                                                {teacher}
                                                            </div>
                                                            <div className="w-[8%] px-3">
                                                                <span
                                                                    className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                                                                        statusBadgeClass[status] || statusBadgeClass.Incomplete
                                                                    }`}
                                                                >
                                                                    {status}
                                                                </span>
                                                            </div>
                                                            <div className="flex w-[16%] min-w-[220px] shrink-0 items-center gap-2 px-4">
                                                                {renderRowActions(item, (e) => e.stopPropagation())}
                                                                <ChevronDown
                                                                    size={16}
                                                                    className={`shrink-0 text-[#94a3b8] transition ${
                                                                        expanded ? 'rotate-180' : ''
                                                                    }`}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={`grid transition-all duration-300 ease-out ${
                                                                expanded
                                                                    ? 'grid-rows-[1fr] opacity-100'
                                                                    : 'grid-rows-[0fr] opacity-0'
                                                            }`}
                                                        >
                                                            <div className="overflow-hidden">
                                                                {expanded ? renderExpanded(item) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Mobile cards */}
                    <div className="space-y-3 lg:hidden">
                        {pageItems.map((item) => {
                            const prog = latestProgram(item);
                            const status = getChildStatus(item, prog);
                            const expanded = expandedId === item.id;
                            return (
                                <div
                                    key={item.id}
                                    className="overflow-hidden rounded-[20px] border border-[#efe6f1] bg-white shadow-sm"
                                >
                                    <button
                                        type="button"
                                        className="flex w-full items-start gap-3 p-4 text-left"
                                        onClick={() => setExpandedId(expanded ? null : item.id)}
                                    >
                                        <ChildAvatar firstName={item.firstName} lastName={item.lastName} />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className="font-semibold capitalize text-ryd-headerTextPrimary">
                                                    {item.firstName} {item.lastName}
                                                </p>
                                                <span
                                                    className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                                                        statusBadgeClass[status] || statusBadgeClass.Incomplete
                                                    }`}
                                                >
                                                    {status}
                                                </span>
                                            </div>
                                            <p className="mt-1 text-[12px] text-[#8b9aab]">
                                                Age {calculateExactAge(item?.age, item?.createdAt)}
                                                {prog?.package?.title ? ` · ${prog.package.title}` : ''}
                                            </p>
                                            <div className="mt-3 flex flex-nowrap items-center gap-1.5 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
                                                {renderRowActions(item, (e) => e.stopPropagation())}
                                            </div>
                                        </div>
                                        <ChevronDown
                                            size={18}
                                            className={`mt-1 shrink-0 text-[#94a3b8] transition ${expanded ? 'rotate-180' : ''}`}
                                        />
                                    </button>
                                    {expanded ? renderExpanded(item) : null}
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-[13px] text-[#8b9aab]">
                            Showing {rangeStart}–{rangeEnd} of {filtered.length} children
                        </p>
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                disabled={currentPage <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="inline-flex h-9 items-center gap-1 rounded-xl border border-[#efe6f1] bg-white px-3 text-[13px] font-medium text-[#475569] disabled:opacity-40"
                            >
                                <ChevronLeft size={16} /> Prev
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter((n) => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
                                .reduce<(number | '…')[]>((acc, n, idx, arr) => {
                                    if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push('…');
                                    acc.push(n);
                                    return acc;
                                }, [])
                                .map((n, i) =>
                                    n === '…' ? (
                                        <span key={`e-${i}`} className="px-1 text-[#94a3b8]">…</span>
                                    ) : (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setPage(n as number)}
                                            className={`flex h-9 w-9 items-center justify-center rounded-xl text-[13px] font-semibold transition ${
                                                currentPage === n
                                                    ? 'bg-ryd-primary text-white shadow-sm'
                                                    : 'border border-[#efe6f1] bg-white text-[#475569] hover:border-ryd-primary'
                                            }`}
                                        >
                                            {n}
                                        </button>
                                    )
                                )}
                            <button
                                type="button"
                                disabled={currentPage >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                className="inline-flex h-9 items-center gap-1 rounded-xl border border-[#efe6f1] bg-white px-3 text-[13px] font-medium text-[#475569] disabled:opacity-40"
                            >
                                Next <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </>
            )}

            {toggleDel && (
                <CustomModal
                    modalStyle={`${modalShell} max-w-md`}
                    closeModal={() => setToggleDel(false)}
                >
                    <div className={`relative ${modalBody} text-center`}>
                        <button
                            type="button"
                            aria-label="Close"
                            className={modalCloseBtn}
                            onClick={() => setToggleDel(false)}
                        >
                            <X size={18} />
                        </button>
                        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                            <Trash2 size={20} />
                        </div>
                        <h1 className={modalTitle}>Remove child?</h1>
                        <p className={modalSubtitle}>
                            Are you sure you want to remove{' '}
                            <span className="font-semibold capitalize text-ryd-headerTextPrimary">
                                {selectedChild?.firstName} {selectedChild?.lastName}
                            </span>
                            ? This cannot be undone.
                        </p>
                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
                            <button
                                type="button"
                                onClick={() => setToggleDel(false)}
                                className={modalSecondaryBtn}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={deleting}
                                onClick={handleDelete}
                                className={modalDangerBtn}
                            >
                                {deleting ? 'Removing…' : 'Yes, remove'}
                            </button>
                        </div>
                    </div>
                </CustomModal>
            )}

            {toggleCertificate && (
                <CustomModal
                    modalStyle="relative bg-white w-full max-w-2xl mx-auto rounded-[20px] max-h-[85vh] overflow-y-auto"
                    closeModal={() => setToggleCertificate(false)}
                >
                    <div className="p-4">
                        <ProgramsList programs={programs} />
                        <div className="mt-4 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setToggleCertificate(false)}
                                className="rounded-xl border border-red-700 px-4 py-2 text-[13px] text-red-700"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </CustomModal>
            )}

            {toggleReceipt && (
                <CustomModal
                    modalStyle="relative bg-white w-full max-w-2xl mx-auto rounded-[20px] max-h-[85vh] overflow-y-auto"
                    closeModal={() => setToggleReceipt(false)}
                >
                    <div className="p-4">
                        <ReceiptList programs={paidPrograms} childData={selectedChild} />
                        <div className="mt-4 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setToggleReceipt(false)}
                                className="rounded-xl border border-red-700 px-4 py-2 text-[13px] text-red-700"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </CustomModal>
            )}
        </AppLayout>
    );
}
